import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Library, Search, Loader2, Wand2, Box, Image as ImageIcon, Layers, User, Sparkles, GitFork, GitBranch, Store, Globe, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ART_STYLES, STYLE_LABEL } from "@/lib/styles";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KINDS = [
  { key: "all", label: "All", icon: Layers },
  { key: "image", label: "Images", icon: ImageIcon },
  { key: "texture", label: "Textures", icon: Layers },
  { key: "3d", label: "3D Models", icon: Box },
  { key: "character", label: "Characters", icon: User },
];
const MARKET_KINDS = KINDS.filter((k) => k.key !== "character");

function assetSrc(a) {
  const u = a?.thumb || a?.url;
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND}${u}`;
}

function KindPill({ kind }) {
  const label = kind === "3d" ? "3D" : kind[0].toUpperCase() + kind.slice(1);
  return <span className="absolute top-2 left-2 rounded-full bg-black/60 backdrop-blur px-2 py-0.5 text-[10px] font-bold text-white">{label}</span>;
}

function flattenTree(node, depth, out) {
  if (!node) return out;
  out.push({ ...node, _depth: depth });
  (node.children || []).forEach((c) => flattenTree(c, depth + 1, out));
  return out;
}

export default function AssetUniverse() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("library");
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [market, setMarket] = useState([]);
  const [marketLoaded, setMarketLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [publishingId, setPublishingId] = useState(null);

  const [reskinFor, setReskinFor] = useState(null); // { id, kind, name, style, market }
  const [reskinStyle, setReskinStyle] = useState("");
  const [reskinning, setReskinning] = useState(false);

  const [lineageFor, setLineageFor] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const pollRef = useRef(null);

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get(`/asset-universe/library`, { params: { q: "", kind: "all" } });
      setAssets(data?.assets || []);
    } catch (e) { toast.error("Couldn't load your Asset Universe."); }
    finally { setLoading(false); }
  }, []);

  const loadMarket = useCallback(async () => {
    try {
      const { data } = await api.get(`/asset-universe/marketplace`, { params: { sort: "popular" } });
      setMarket(data?.assets || []);
      setMarketLoaded(true);
    } catch (e) { toast.error("Couldn't load the marketplace."); }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);
  useEffect(() => { if (tab === "market" && !marketLoaded) loadMarket(); }, [tab, marketLoaded, loadMarket]);

  useEffect(() => {
    const anyProcessing = assets.some((a) => a.kind === "3d" && a.status && a.status !== "succeeded" && a.status !== "failed");
    if (anyProcessing) {
      pollRef.current = setInterval(loadLibrary, 6000);
      return () => clearInterval(pollRef.current);
    }
  }, [assets, loadLibrary]);

  const source = tab === "library" ? assets : market;
  const activeKinds = tab === "library" ? KINDS : MARKET_KINDS;

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return source.filter((a) => {
      if (kind !== "all" && a.kind !== kind) return false;
      if (!ql) return true;
      return (a.name || "").toLowerCase().includes(ql) || (a.prompt || "").toLowerCase().includes(ql) || (a.creator || "").toLowerCase().includes(ql);
    });
  }, [source, q, kind]);

  const counts = useMemo(() => {
    const c = { all: source.length };
    for (const a of source) c[a.kind] = (c[a.kind] || 0) + 1;
    return c;
  }, [source]);

  const openReskin = (a, isMarket = false) => { setReskinFor({ ...a, market: isMarket }); setReskinStyle(a.style === "render3d" ? "realistic" : "render3d"); };
  const closeReskin = () => { if (!reskinning) { setReskinFor(null); setReskinStyle(""); } };

  const doReskin = async () => {
    if (!reskinFor || !reskinStyle) { toast.error("Pick a style first."); return; }
    setReskinning(true);
    try {
      if (reskinFor.market) {
        const { data } = await api.post(`/asset-universe/marketplace/${reskinFor.id}/remix`, { kind: reskinFor.kind, style: reskinStyle }, { timeout: 120000 });
        toast.success(data?.kind === "3d" ? `🧊 Remixing in ${STYLE_LABEL[reskinStyle]} — building your 3D model…` : `✨ Remixed into your library (${STYLE_LABEL[reskinStyle]})! Credit: ${data?.creator || "creator"}.`);
        setMarket((l) => l.map((x) => (x.id === reskinFor.id ? { ...x, remix_count: (x.remix_count || 0) + 1 } : x)));
        const added = data?.asset || data?.model;
        if (added) setAssets((l) => [added, ...l]);
      } else {
        const { data } = await api.post(`/asset-universe/reskin`, { asset_id: reskinFor.id, kind: reskinFor.kind, style: reskinStyle }, { timeout: 120000 });
        if (data?.kind === "3d") {
          toast.success(`🧊 Reskinning "${reskinFor.name}" in ${STYLE_LABEL[reskinStyle]} — building your 3D model…`);
          setAssets((l) => [data.model, ...l]);
        } else {
          toast.success(`✨ Reskinned to ${STYLE_LABEL[reskinStyle]}!`);
          setAssets((l) => [data.asset, ...l.map((x) => (x.id === reskinFor.id ? { ...x, remix_count: (x.remix_count || 0) + 1 } : x))]);
        }
      }
      setReskinFor(null); setReskinStyle("");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't reskin that asset.");
    } finally { setReskinning(false); }
  };

  const togglePublish = async (a) => {
    setPublishingId(a.id);
    const next = !a.published;
    try {
      await api.post(`/asset-universe/${a.id}/${next ? "publish" : "unpublish"}`, { kind: a.kind });
      setAssets((l) => l.map((x) => (x.id === a.id ? { ...x, published: next } : x)));
      toast.success(next ? "🌍 Published to the marketplace!" : "Removed from the marketplace.");
      if (marketLoaded) loadMarket();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't update publish status.");
    } finally { setPublishingId(null); }
  };

  const openLineage = async (a) => {
    setLineageFor(a); setLineage(null); setLineageLoading(true);
    try {
      const { data } = await api.get(`/asset-universe/${a.id}/lineage`);
      setLineage(data);
    } catch (e) { toast.error("Couldn't load the remix family tree."); }
    finally { setLineageLoading(false); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="asset-universe">
      <PageHeader
        title="Asset Universe"
        subtitle="Every AI asset you've ever made — search it, reskin any of it into a new style, publish to the marketplace, or remix what other creators share."
        icon={Library}
      />

      {/* Tabs */}
      <div className="inline-flex rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1 mb-5" data-testid="universe-tabs">
        <button
          onClick={() => { setTab("library"); setKind("all"); setQ(""); }}
          data-testid="universe-tab-library"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "library" ? "bg-violet-600 text-white" : "text-[hsl(var(--muted-foreground))]"}`}
        ><Library className="w-4 h-4" /> My Library</button>
        <button
          onClick={() => { setTab("market"); setKind("all"); setQ(""); }}
          data-testid="universe-tab-market"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "market" ? "bg-violet-600 text-white" : "text-[hsl(var(--muted-foreground))]"}`}
        ><Store className="w-4 h-4" /> Marketplace</button>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "library" ? "Search your assets by name or prompt…" : "Search the marketplace by name, prompt or creator…"}
            data-testid="universe-search"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] pl-10 pr-4 py-2.5 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="universe-filters">
          {activeKinds.map((k) => {
            const on = kind === k.key;
            const Icon = k.icon;
            return (
              <button key={k.key} onClick={() => setKind(k.key)} data-testid={`universe-filter-${k.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${on ? "border-violet-500 bg-violet-500/10 text-violet-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
                <Icon className="w-3.5 h-3.5" /> {k.label}<span className="text-[10px] opacity-70">{counts[k.key] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          emoji={tab === "market" ? "🛒" : "🌌"}
          title={tab === "market" ? (q || kind !== "all" ? "No matching assets" : "Marketplace is warming up") : (q || kind !== "all" ? "No matching assets" : "Your universe is empty")}
          text={tab === "market" ? "Publish one of your own assets from My Library to get the marketplace going — then anyone can remix it." : (q || kind !== "all" ? "Try a different search or filter." : "Generate images in Asset Studio, characters in Character Studio or models in 3D Studio — they'll all show up here, ready to reskin.")}
          action={tab === "library" ? <Button className="rounded-full" onClick={() => navigate("/app/assets")} data-testid="universe-goto-studio"><Sparkles className="w-4 h-4" /> Create an asset</Button> : <Button variant="outline" className="rounded-full" onClick={() => setTab("library")}><Library className="w-4 h-4" /> Go to My Library</Button>}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="universe-grid">
          {filtered.map((a) => {
            const src = assetSrc(a);
            const processing = a.kind === "3d" && a.status && a.status !== "succeeded" && a.status !== "failed";
            const isMarket = tab === "market";
            const canLineage = !!a.remix_of || (a.remix_count || 0) > 0;
            return (
              <div key={`${a.kind}-${a.id}`} className="group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden flex flex-col" data-testid={`universe-asset-${a.id}`}>
                <div className="relative aspect-square bg-[hsl(var(--secondary))] grid place-items-center overflow-hidden">
                  <KindPill kind={a.kind} />
                  {a.published && !isMarket && <span className="absolute top-2 right-2 rounded-full bg-emerald-600/90 px-2 py-0.5 text-[10px] font-bold text-white flex items-center gap-1"><Globe className="w-3 h-3" /> Live</span>}
                  {a.remix_of && !isMarket && <span className="absolute top-2 right-2 rounded-full bg-fuchsia-600/90 px-2 py-0.5 text-[10px] font-bold text-white flex items-center gap-1"><GitFork className="w-3 h-3" /> Remix</span>}
                  {isMarket && a.mine && <span className="absolute top-2 right-2 rounded-full bg-sky-600/90 px-2 py-0.5 text-[10px] font-bold text-white">Yours</span>}
                  {processing ? (
                    <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))]"><Loader2 className="w-6 h-6 animate-spin" /><span className="text-[11px]">Building… {a.progress || 0}%</span></div>
                  ) : src ? (
                    <img src={src} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  ) : (<Box className="w-10 h-10 text-[hsl(var(--muted-foreground))]" />)}
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" title={a.name}>{a.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {a.style && <span className="text-[10px] rounded-full bg-[hsl(var(--secondary))] px-2 py-0.5 text-[hsl(var(--muted-foreground))]">{STYLE_LABEL[a.style] || a.style}</span>}
                      {isMarket && a.creator && <span className="text-[10px] text-[hsl(var(--muted-foreground))]">by {a.creator}</span>}
                      {(a.remix_count || 0) > 0 && <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{a.remix_count} remix{a.remix_count === 1 ? "" : "es"}</span>}
                    </div>
                  </div>
                  <div className="mt-auto flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full flex-1" disabled={a.reskinnable === false || processing}
                      onClick={() => openReskin(a, isMarket)} data-testid={`universe-reskin-${a.id}`}>
                      <Wand2 className="w-4 h-4" /> {isMarket ? "Remix" : "Reskin"}
                    </Button>
                    {!isMarket && (a.kind === "image" || a.kind === "texture" || a.kind === "3d") && (
                      <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" disabled={publishingId === a.id || processing}
                        onClick={() => togglePublish(a)} title={a.published ? "Unpublish" : "Publish to marketplace"} data-testid={`universe-publish-${a.id}`}>
                        {publishingId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (a.published ? <Globe className="w-4 h-4 text-emerald-500" /> : <Lock className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />)}
                      </Button>
                    )}
                    {!isMarket && canLineage && (
                      <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => openLineage(a)} title="Remix family tree" data-testid={`universe-lineage-${a.id}`}>
                        <GitBranch className="w-4 h-4 text-fuchsia-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reskin / Remix dialog */}
      <Dialog open={!!reskinFor} onOpenChange={(v) => { if (!v) closeReskin(); }}>
        <DialogContent className="max-w-lg" data-testid="universe-reskin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-violet-500" /> {reskinFor?.market ? "Remix" : "Reskin"} "{reskinFor?.name}"</DialogTitle>
            <DialogDescription>
              {reskinFor?.market
                ? <>Generate your own {reskinFor?.kind === "3d" ? "3D model" : "version"} of this creator's asset in a new style — it lands in your library with credit to {reskinFor?.creator || "the original creator"}.</>
                : <>Generate a brand-new {reskinFor?.kind === "3d" ? "3D model" : reskinFor?.kind === "character" ? "character portrait" : "version"} of this asset in a different art style. Your original stays untouched.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[46vh] overflow-y-auto py-1">
            {ART_STYLES.map((s) => {
              const on = reskinStyle === s.id;
              return (
                <button key={s.id} onClick={() => setReskinStyle(s.id)} disabled={reskinning} data-testid={`universe-style-${s.id}`}
                  className={`rounded-xl border p-2.5 text-center transition-all ${on ? "border-violet-500 ring-2 ring-violet-500/40" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
                  <div className="h-9 rounded-lg mb-1.5" style={{ background: s.swatch }} />
                  <div className="text-lg leading-none">{s.emoji}</div>
                  <div className="text-[11px] font-semibold mt-1">{s.label}</div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{reskinFor?.kind === "3d" ? "Uses 20 AI credits · builds in the background" : "Uses 5 AI credits"}</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={closeReskin} disabled={reskinning}>Cancel</Button>
              <Button size="sm" className="rounded-full" onClick={doReskin} disabled={reskinning || !reskinStyle} data-testid="universe-reskin-confirm">
                {reskinning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {reskinning ? (reskinFor?.market ? "Remixing…" : "Reskinning…") : (reskinFor?.market ? "Remix it" : "Reskin it")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lineage dialog */}
      <Dialog open={!!lineageFor} onOpenChange={(v) => { if (!v) { setLineageFor(null); setLineage(null); } }}>
        <DialogContent className="max-w-lg" data-testid="universe-lineage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitBranch className="w-5 h-5 text-fuchsia-500" /> Remix family tree</DialogTitle>
            <DialogDescription>Every style "{lineageFor?.name}" traces back to and every remix it's inspired.</DialogDescription>
          </DialogHeader>
          {lineageLoading ? (
            <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
          ) : lineage?.root ? (
            <div className="max-h-[56vh] overflow-y-auto py-1" data-testid="lineage-tree">
              {flattenTree(lineage.root, 0, []).map((n) => {
                const src = assetSrc(n);
                return (
                  <div key={`${n.kind}-${n.id}`} style={{ marginLeft: n._depth * 20 }} data-testid={`lineage-node-${n.id}`}
                    className={`flex items-center gap-2.5 py-1.5 my-0.5 ${n._depth > 0 ? "border-l-2 border-[hsl(var(--border))] pl-3" : ""} ${n.is_focus ? "bg-violet-500/10 rounded-lg px-2 ring-1 ring-violet-500/40" : ""}`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[hsl(var(--secondary))] grid place-items-center shrink-0">
                      {src ? <img src={src} alt={n.name} className="w-full h-full object-cover" /> : <Box className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                        {n.name}
                        {n.is_focus && <span className="text-[10px] rounded-full bg-violet-500/20 text-violet-500 px-1.5 py-0.5">this one</span>}
                      </p>
                      <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">
                        {n.style && <span className="rounded-full bg-[hsl(var(--secondary))] px-1.5">{STYLE_LABEL[n.style] || n.style}</span>}
                        <span>· {n.creator}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3">{lineage.total} asset{lineage.total === 1 ? "" : "s"} in this family.</p>
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">No lineage yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
